import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  SKILL_V2_SELECTION_LIST_QUERY,
  TaxonomyType,
} from '@revelio/data-access';
import type { SelectionList } from '@revelio/iso-utility';

import { useTaxonomyTree } from '../../../shared/selection-lists/hooks/useTaxonomyTree';
import { treeToParentLinkedSelectionLists } from '../../../shared/selection-lists/utils/treeToParentLinkedSelectionLists';
import { isV3Taxonomy } from '../utils/job-taxonomy-version-migration';

// The skill taxonomy tree exposes levels skill_k15…skill_k35000. DataBuilder's
// V3 skill keys map to a subset of them by tag; the tree collapses the unused
// k15/k50/k500 levels when they aren't requested (same as CustomDash relies on).
const SKILL_TREE_LEVELS = [3, 5, 6, 7, 8];

/**
 * Fetches skill selection lists for Data Builder. V3 resolves from the taxonomy
 * tree (TaxonomyType.Skill) honouring the selected custom skill taxonomy id; V2
 * keeps using the legacy skill selection-list query, mirroring how
 * useRoleSelectionLists handles V2 roles. DataBuilder is the only consumer of
 * the V2 skill query — CustomDash / reports source skill from the adaptive
 * custom-skill tree, not from here.
 */
export const useSkillSelectionLists = (
  requiredSkillLists: string[] = [],
  options: { taxonomyId?: number; skillTaxonomyVersion?: string }
) => {
  const isV3 = isV3Taxonomy(options.skillTaxonomyVersion);
  const taxonomyId = options.taxonomyId ?? 0;

  const { treeData, levels, fetching } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Skill,
    taxonomyId,
    levels: SKILL_TREE_LEVELS,
    pause: !isV3,
  });

  const [{ data: legacyData, fetching: legacyFetching }] = useQuery({
    query: SKILL_V2_SELECTION_LIST_QUERY,
    pause: isV3 || requiredSkillLists.length === 0,
  });

  const transformedSkillLists = useMemo(() => {
    const result: Record<string, SelectionList> = {};

    if (isV3) {
      if (treeData.length === 0) return result;

      // The tree tags (skill_k150, skill_k1500, …) are exactly DataBuilder's V3
      // skill keys, so each requested key reads straight off the converted lists.
      const lists = treeToParentLinkedSelectionLists(treeData, levels);
      requiredSkillLists.forEach((listId) => {
        const items = lists[listId];
        if (items) {
          result[listId] = {
            id: listId,
            label: listId,
            value: items,
          } as SelectionList;
        }
      });
    } else if (legacyData?.selectionList) {
      requiredSkillLists.forEach((listId) => {
        const skillList =
          legacyData.selectionList?.[
            listId as keyof NonNullable<typeof legacyData.selectionList>
          ];

        if (skillList && Array.isArray(skillList)) {
          result[listId] = {
            id: listId,
            label: listId,
            value: skillList,
          } as SelectionList;
        }
      });
    }

    return result;
  }, [isV3, treeData, levels, legacyData, requiredSkillLists]);

  return {
    skillSelectionLists: transformedSkillLists,
    isV3Taxonomy: isV3,
    taxonomyId,
    isLoading: fetching || legacyFetching,
  };
};
